import * as React from 'react';
import { Link } from 'react-router';
import ReactHtmlParser from 'react-html-parser';
import * as Modal from 'react-modal';

import {
    HistoricTournament,
    TournamentEvent,
    PlayerPosition,
    HistoricPlayer,
    TournamentTemplateReference,
    TournamentEventType,
    FinalPayout,
    Account,
} from '../../../../generated/protocol';
import { formatDate } from '../../util/date';

interface HistoricTournamentProps {
    tournament: HistoricTournament;
    linkedTournaments?: HistoricTournament[];
    togglePositions?: () => void;
    toggleEvents?: () => void;
    showAllPositions?: boolean;
    showAllEvents?: boolean;
    showAllRegistered?: boolean;
    toggleRegistered?: () => void;
    playerOutMap?: Map<number, TournamentEvent>;
}

export class HistoricTournamentComponent extends React.Component<HistoricTournamentProps, {}> {
    createPlayerMap(registeredPlayers: HistoricPlayer[]): Map<number, HistoricPlayer> {
        const playerMap = new Map<number, HistoricPlayer>();
        registeredPlayers.forEach(p => playerMap.set(p.id, p));
        return playerMap;
    }

    render(): JSX.Element  {
        const tournament = this.props.tournament;
        const linkedTournaments = this.props.linkedTournaments;

        const playerMap = this.createPlayerMap(tournament.registeredPlayers);
        const playerOutMap = this.props.playerOutMap;

        return (
            <div>
                <div className='row'>
                    <div className='col-md-12'>
                        <div className='ibox float-e-margins'>
                            <div className='ibox-title'>
                                <h5>Summary</h5>
                            </div>
                            <div className='ibox-content'>
                                <HistoricTournamentSummary tournament={tournament}
                                                           linkedTournaments={linkedTournaments}/>
                            </div>

                        </div>
                    </div>
                </div>

                <div className='ibox float-e-margins '>
                    <div className='ibox-title'>
                        <h5>Registered Players</h5>
                    </div>
                    <div className='ibox-content'>
                        <RegisteredPlayers players={tournament.registeredPlayers} events={tournament.events}
                                           showAll={this.props.showAllRegistered}/>
                        <a className='btn btn-primary' onClick={this.props.toggleRegistered}>
                            {this.props.showAllRegistered ? 'Show 10' : 'Show All Players'}
                        </a>
                    </div>
                </div>
                <div className='ibox float-e-margins '>
                    <div className='ibox-title'>
                        <h5>Results</h5>
                    </div>
                    <div className='ibox-content'>
                        <TournamentResults positions={tournament.positions} finalPayouts={tournament.finalPayouts} playerMap={playerMap}
                                           playerOutMap={playerOutMap} showAll={this.props.showAllPositions}/>
                        <a className='btn btn-primary' onClick={this.props.togglePositions}>
                            {this.props.showAllPositions ? 'Show Top 10' : 'Show All Positions'}
                        </a>
                    </div>
                </div>

                <div className='ibox float-e-margins '>
                    <div className='ibox-title'>
                        <h5>Events</h5>
                    </div>
                    <div className='ibox-content' style={{overflowX: 'scroll'}}>
                        <a className='btn btn-primary' onClick={this.props.toggleEvents}>
                            {this.props.showAllEvents ? 'Show only from Running' : 'Show Previous Events'}
                        </a>
                        <TournamentEvents events={tournament.events} playerMap={playerMap}
                                          showAll={this.props.showAllEvents}/>
                    </div>
                </div>
            </div>
        );
    }
};


interface HistoricTournamentSummaryState {
    showDescription: boolean;
}

export class HistoricTournamentSummary extends React.Component<HistoricTournamentProps, HistoricTournamentSummaryState> {
    static modalStyle = {
        content: {
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            marginRight: '-50%',
            transform: 'translate(-50%, -50%)',
        },
    };

    constructor() {
        super();
        this.state = {'showDescription': false};
    }

    getTournamentLink(tournament: HistoricTournament): string {
        if (tournament.sitAndGo) {
            return '/sit-and-go/' + tournament.tournamentTemplateId;
        } else {
            return '/tournament/' + tournament.tournamentTemplateId;
        }
    }

    renderTTRef(satelliteTarget: HistoricTournament, ttRef: TournamentTemplateReference, index: number): JSX.Element {
        return (
            <div key={index}>
                <Link to={'/historic-tournament/' + satelliteTarget.historicId}>{ttRef.tournamentName}</Link>
                &nbsp; ({ttRef.buyIn}+{ttRef.fees})
                &nbsp; {ttRef.noOfRebuys == 0 ? '' : ttRef.noOfRebuys + 'R'}{ttRef.noOfAddons == 0 ? '' : ttRef.noOfAddons + 'A'}
                &nbsp; {formatDate(satelliteTarget.endTime)}
            </div>
        );
    }

    satelliteTargets(linkedTournaments: HistoricTournament[], tournament: HistoricTournament): JSX.Element {
        if (tournament.satelliteTargets && tournament.satelliteTargets.length > 0 && linkedTournaments && linkedTournaments.length > 0) {
            const satelliteTargetTemplateIds: Map<number, TournamentTemplateReference> = new Map<number, TournamentTemplateReference>();

            tournament.satelliteTargets.forEach((ttRef: TournamentTemplateReference) => {
                satelliteTargetTemplateIds.set(ttRef.tournamentTemplateId, ttRef);
            });
            return (
                <tr>
                    <td>Linked Targets</td>
                    <td>{
                        linkedTournaments
                            .filter((ht: HistoricTournament) => {
                                return satelliteTargetTemplateIds.has(ht.tournamentTemplateId);
                            })
                            .map((ht: HistoricTournament, index: number) => {
                                return this.renderTTRef(ht, satelliteTargetTemplateIds.get(ht.tournamentTemplateId), index);
                            })
                    }</td>
                </tr>
            );
        }
        else {
            return;
        }

    }

    targetSatellites(linkedTournaments: HistoricTournament[], tournament: HistoricTournament): JSX.Element {
        if (tournament.targetSatellites && tournament.targetSatellites.length > 0 && linkedTournaments && linkedTournaments.length > 0) {
            const targetSatellitesTemplateIds: Map<number, TournamentTemplateReference> = new Map<number, TournamentTemplateReference>();

            tournament.targetSatellites.forEach((ttRef: TournamentTemplateReference) => {
                targetSatellitesTemplateIds.set(ttRef.tournamentTemplateId, ttRef);
            });
            return (
                <tr>
                    <td>Linked Satellites</td>
                    <td>{
                        linkedTournaments
                            .filter((ht: HistoricTournament) => {
                                return targetSatellitesTemplateIds.has(ht.tournamentTemplateId);
                            })
                            .map((ht: HistoricTournament, index: number) => {
                                return this.renderTTRef(ht, targetSatellitesTemplateIds.get(ht.tournamentTemplateId), index);
                            })
                    }</td>
                </tr>
            );
        }
        else {
            return;
        }
    }

    countEvents(type: TournamentEventType): number {
        return this.props.tournament.events.filter((event) => (event.eventType == type)).length;
    }

    renderGeneratedPrizepool(tournament: HistoricTournament): JSX.Element {
        if (!tournament.buyIn) return;

        const addons = this.countEvents('ADD_ON_PERFORMED');
        const rebuys = this.countEvents('REBUY_PERFORMED');

        if (addons > 0 && tournament.addOnCost == null) return;
        if (rebuys > 0 && tournament.rebuyCost == null) return;

        const generated = (tournament.registeredPlayers.length*tournament.buyIn)+(addons*tournament.addOnCost)+(rebuys*tournament.rebuyCost);

        return (
            <tr>
                <td>Generated Prize Pool</td>
                <td>{generated.toLocaleString()}</td>
            </tr>
        );
    }

    renderDescription(tournament: HistoricTournament): JSX.Element {
        if (!tournament.tournamentDesc) return;

        return (
            <tr>
                <td>Description</td>
                <td>
                    <button className={'btn btn-primary'} onClick={() => (this.setState({'showDescription': true}))}>
                        <i className='fa fa-eye'/> View
                    </button>
                </td>
            </tr>
        );
    }

    renderBuyInAndFee(tournament: HistoricTournament): JSX.Element  {
        if(!tournament.buyIn && !tournament.fee) return;

        return (
            <tr>
                <td>Buy-In + Fee</td>
                <td>{tournament.buyIn + ' + ' + tournament.fee}</td>
            </tr>
        );
    }

    renderRebuy(tournament: HistoricTournament): JSX.Element  {
        if(!tournament.rebuyCost) return;

        const rebuyCount = this.countEvents( 'REBUY_PERFORMED');
        const rebuyCost = tournament.rebuyCost.toLocaleString()
        const rebuyString = `${rebuyCount} (Amount: ${rebuyCost})`;

        return (
            <tr>
                <td>Number of Rebuy</td>
                <td>{rebuyString}</td>
            </tr>
        );
    }

    renderAddon(tournament: HistoricTournament): JSX.Element  {
        if (!tournament.addOnCost) return;

        const addOnCount = this.countEvents( 'ADD_ON_PERFORMED');
        const addOnCost = tournament.addOnCost.toLocaleString();
        const addOnString = `${addOnCount} (Amount: ${addOnCost})`;

        return (
            <tr>
                <td>Number of Add-on</td>
                <td>{addOnString}</td>
            </tr>
        );
    }

    renderGuaranteedPrizePool(tournament: HistoricTournament): JSX.Element  {
        if (!tournament.guaranteedPrizePool) return;

        return (
            <tr>
                <td>Guaranteed Prize Pool</td>
                <td>{tournament.guaranteedPrizePool.toLocaleString()}</td>
            </tr>
        );
    }

    renderPrizeBoostPool(tournament: HistoricTournament): JSX.Element  {
        if (!tournament.prizeBoostPool) return;

        const {prizeBoostPool: {
            cash,
            ticketCount,
            ticketNameId,
            ticketPrice,
        }} = tournament;

        if(ticketCount > 0) {
            const information = `${ticketNameId} X ${ticketCount} (${ticketPrice.toFixed(2)})`;
            return (
                <tr>
                    <td>Prize boost</td>
                    <td>{information}</td>
                </tr>
            );
        }

        return (
            <tr>
                <td>Prize boost</td>
                <td>{cash.toFixed(2).toLocaleString()}</td>
            </tr>
        );
    }

    renderPrizePool(tournament: HistoricTournament): JSX.Element  {
        if (!tournament.payoutMultiplier || !tournament.buyIn) return;

        const prizePool = tournament.buyIn * tournament.payoutMultiplier;

        return (
            <tr>
                <td>Prize Pool</td>
                <td>{prizePool}</td>
            </tr>
        );
    }

    renderPayoutMultiplier(tournament: HistoricTournament): JSX.Element  {
        if (!tournament.payoutMultiplier) return;

        return (
            <tr>
                <td>Payout Multiplier</td>
                <td>{tournament.payoutMultiplier}</td>
            </tr>
        );
    }

    renderCurrency(account: Account): JSX.Element  {
        if (!account) return;

        return (
            <tr>
                <td>Currency</td>
                <td>{account.currency}</td>
            </tr>
        );
    }

    static getTournamentTypeFromTournament(tournament) {
        if(tournament.sitAndGo) {
            return 'Sit And Go';
        } else if(tournament.jackpotSitAndGo) {
            return 'Jackpot Sit And Go';
        } else {
            return 'Scheduled Tournament';
        }
    }

    render(): JSX.Element  {
        const tournament: HistoricTournament = this.props.tournament;
        const linkedTournaments: HistoricTournament[] = this.props.linkedTournaments;
        const account: Account = this.props.account;

        return (
            <div>
                <table className='table table-striped table-info'>
                    <tbody>
                    <tr>
                        <td>Historic ID</td>
                        <td>{tournament.id}</td>
                    </tr>
                    <tr>
                        <td>Name</td>
                        <td>{tournament.tournamentName}</td>
                    </tr>
                    {this.renderDescription(tournament)}
                    {this.renderBuyInAndFee(tournament)}
                    {this.renderPayoutMultiplier(tournament)}
                    <tr>
                        <td>Registered Players</td>
                        <td>{tournament.registeredPlayers.length}</td>
                    </tr>
                    {this.renderRebuy(tournament)}
                    {this.renderAddon(tournament)}
                    {this.renderPrizePool(tournament)}
                    {this.renderGeneratedPrizepool(tournament)}
                    {this.renderGuaranteedPrizePool(tournament)}
                    {this.renderPrizeBoostPool(tournament)}
                    {this.renderCurrency(account)}
                    <tr>
                        <td>Scheduled Start Time</td>
                        <td>{formatDate(tournament.scheduledStartTime)}</td>
                    </tr>
                    <tr>
                        <td>Start Time</td>
                        <td>{formatDate(tournament.startTime)}</td>
                    </tr>
                    <tr>
                        <td>End Time</td>
                        <td>{formatDate(tournament.endTime)}</td>
                    </tr>
                    <tr>
                        <td>Status</td>
                        <td>{tournament.status}</td>
                    </tr>
                    <tr>
                        <td>Template ID</td>
                        <td><Link to={this.getTournamentLink(tournament)}>{tournament.tournamentTemplateId}</Link></td>
                    </tr>
                    <tr>
                        <td>Tournament Type</td>
                        <td>{HistoricTournamentSummary.getTournamentTypeFromTournament(tournament)}</td>
                    </tr>
                    {this.satelliteTargets(linkedTournaments, tournament)}
                    {this.targetSatellites(linkedTournaments, tournament)}
                    </tbody>
                </table>
                <Modal isOpen={this.state.showDescription}  style={HistoricTournamentSummary.modalStyle}>
                    <div className='panel panel-default'>
                        <div className='panel-body'>{ReactHtmlParser(this.props.tournament.tournamentDesc)}</div>
                    </div>
                    <button className={'btn btn-danger'} onClick={() => (this.setState({'showDescription': false}))}>Close</button>
                </Modal>
            </div>
        );
    }
}

export const TournamentEvents = (props: { events: TournamentEvent[], playerMap: Map<number, HistoricPlayer>, showAll?: boolean }) => {
    let startEventRendered = false;
    return (
        <table className='table table-striped tournament-events-table'>
            <thead>
            <tr>
                <th style={{width: '30%'}}>Event</th>
                <th>Value</th>
                <th>Time</th>
            </tr>
            </thead>
            <tbody>
            {props.events.map((event, index) => {
                if (event.details == 'RUNNING') {
                    startEventRendered = true;
                }
                if (!props.showAll && !startEventRendered) {
                    return false;
                }

                return getRowFromEvent(event, index, props.playerMap);
            })
            }
            </tbody>
        </table>
    );
};

function getRowFromEvent(event: TournamentEvent, index: number, playerMap: Map<number, HistoricPlayer>) {
    const details = event.details;
    const eventStr = event.event;
    try {
        if ('' + parseInt(details) == details && eventStr.indexOf(' out') !== -1 && eventStr.indexOf('player ') !== -1) {
            return getPlayerOut(event, index);
        } else if (eventStr.indexOf('player opened') !== -1) {
            return getSessionOpened(event, index, playerMap);
        } else if (details.match('^[A-Z]+$')) {
            return getStatusChanged(event, index);
        } else if ('' + parseInt(details) == details && (eventStr.indexOf('Rebuy') !== -1 || eventStr.indexOf('registered') !== -1 || eventStr.indexOf('Add-on') !== -1)) {
            return getPlayerIdRow(event, index, playerMap);
        }
    } catch (e) {
        console.warn('Unable to render tournament event row for data, rendering default', event);
    }

    return (
        <tr key={index}>
            <td className='tournament-event-type'>{event.event}</td>
            <td className='tournament-event-details'>
                {event.details}
            </td>
            <td className='tournament-event-time'>{formatDate(event.timestamp)}</td>
        </tr>
    )
}

function getPlayerIdRow(event: TournamentEvent, index: number, playerMap: Map<number, HistoricPlayer>) {
    const historicPlayer = playerMap.get(parseInt(event.details));
    return (
        <tr key={index}>
            <td className='tournament-event-type'>
                {event.event}
            </td>
            <td className='tournament-event-details'>
                <span>{historicPlayer && historicPlayer.name} ({event.details}) {getHistoricTournamentLink(event)}</span>
            </td>
            <td className='tournament-event-time'>{formatDate(event.timestamp)}</td>
        </tr>

    );
}

function getSessionOpened(event: TournamentEvent, index: number, playerMap: Map<number, HistoricPlayer>) {
    const match = event.event.match(/([a-zA-Z ]+: )([0-9]+)/);
    if (!match) {
        return <tr></tr>;
    }
    const historicPlayer = playerMap.get(parseInt(event.details));
    return (
        <tr key={index}>
            <td className='tournament-event-type'>
                <span>{match[1]} <a href='#'>{match[2]}</a></span>
            </td>
            <td className='tournament-event-details'>
                <span>{historicPlayer && historicPlayer.name} ({event.details})</span>
            </td>
            <td className='tournament-event-time'>{formatDate(event.timestamp)}</td>
        </tr>

    );
}

function getStatusChanged(event: TournamentEvent, index: number) {
    return (
        <tr key={index} className='status-changed'>
            <td className='tournament-event-type'>{event.event}</td>
            <td className='tournament-event-details'>
                <span className={event.details}>{event.details}</span>
            </td>
            <td className='tournament-event-time'>{formatDate(event.timestamp)}</td>
        </tr>
    );
}

function getHistoricHandLink(event: TournamentEvent) {
    if (event.historicHandId) {
        return <Link to={'/hand/' + event.historicHandId}>[last hand]</Link>
    }
}

function getHistoricTournamentLink(event: TournamentEvent) {
    if (event.historicTournamentId) {
        return <Link to={'/historic-tournament/' + event.historicTournamentId}>[{event.ticketNameId}]</Link>
    }
    else if (event.ticketNameId) {
        return event.ticketNameId;
    }
    else {
        return '';
    }
}

function getEventDetails(event: TournamentEvent) {
    return (
        <div>
            {event.details}
            {getHistoricHandLink(event)}
        </div>
    );
}

function getPlayerOut(event: TournamentEvent, index: number) {
    return (
        <tr key={index} className='player-out'>
            <td className='tournament-event-type'>{event.event}</td>
            <td className='tournament-event-details'>
                <span>{getEventDetails(event)}</span>
            </td>
            <td className='tournament-event-time'>{formatDate(event.timestamp)}</td>
        </tr>
    );
}

export interface TournamentResultsProps {
    positions: PlayerPosition[];
    finalPayouts?: FinalPayout[];
    playerMap: Map<number, HistoricPlayer>;
    playerOutMap: Map<number, TournamentEvent>;
    showAll?: boolean;
}

export interface TournamentResultsState {
    mergedResults: (FinalPayout | PlayerPosition)[];
}

export class TournamentResults extends React.Component<TournamentResultsProps, TournamentResultsState> {

    constructor(props: TournamentResultsProps, context: any) {
        super(props, context);
        this.state = {'mergedResults': this.mergeResults()};
    }

    mergeResults(): (FinalPayout | PlayerPosition)[] {
        const positions = Object.assign([], this.props.positions).reverse();
        if (!this.props.finalPayouts) return positions;
        if (this.props.finalPayouts.length == positions.length) return positions;
        const sliceAmount = this.props.finalPayouts.length-positions.length;
        return this.props.finalPayouts.slice(0, sliceAmount).concat(positions);
    }

    getPayoutDetails(p: PlayerPosition, playerOutMap: Map<number, TournamentEvent>) {
        const outEvent = playerOutMap.get(p.playerId);

        if (p.ticketNameId) {
            if (outEvent && outEvent.historicTournamentId) {
                return (<Link to={'/historic-tournament/' + outEvent.historicTournamentId}>{p.ticketNameId}</Link>);
            }

            return p.ticketNameId;
        }
        else {
            return p.payout;
        }
    }

    renderFinalPayout(payout: FinalPayout): JSX.Element {
        return (
            <tr key={payout.position}>
                <td>{payout.position}</td>
                <td/>
                <td/>
                <td/>
                <td/>
                <td>{this.getPayoutDetails(payout, this.props.playerOutMap)}</td>
            </tr>
        );
    }

    renderPlayerResult(position: PlayerPosition): JSX.Element {
        const player = this.props.playerMap.get(position.playerId);
        return (
            <tr key={position.playerId}>
                <td>{position.position}</td>
                <td><Link to={'/user/' + position.playerId}>{position.playerId}</Link></td>
                <td>{player && player.name}</td>
                <td>{player && player.sessionId}</td>
                <td>{player && (player.pointsBuyIn ? 'Yes' : 'No')}</td>
                <td>{this.getPayoutDetails(position, this.props.playerOutMap)}</td>
            </tr>
        );
    }

    isPlayerPosition(object: (FinalPayout | PlayerPosition)): object is PlayerPosition {
        return 'playerId' in object;
    }

    renderResultRow(row: FinalPayout | PlayerPosition): JSX.Element {
        if (this.isPlayerPosition(row)) {
            return this.renderPlayerResult(row);
        } else {
            return this.renderFinalPayout(row);
        }
    }

    render(): JSX.Element {
        const results = this.state.mergedResults;
        results.sort((a,b) => (a.position > b.position) ? 1 : ((b.position > a.position) ? -1 : 0))
        return (
            <table className='table table-striped tournament-events-table'>
                <thead>
                <tr>
                    <th>Position</th>
                    <th>Player Id</th>
                    <th>Player Name</th>
                    <th>Player Tournament Account</th>
                    <th>Points Buy-in</th>
                    <th>Payout</th>
                </tr>
                </thead>
                <tbody>
                {
                    results.map((position: PlayerPosition | FinalPayout, i) => {
                        if (i > 9 && !this.props.showAll) {
                            return;
                        }

                        return this.renderResultRow(position);
                    })
                }
                </tbody>
            </table>
        );
    }
}

function getBuyIn(player: HistoricPlayer) {
    if (player.ticketNameId) {
        if (player.satelliteTournamentHistoricId) {
            return (
                <Link to={'/historic-tournament/' + player.satelliteTournamentHistoricId}>{player.ticketNameId}</Link>);
        }
        else {
            return player.ticketNameId;
        }
    }
    else if (player.pointsBuyIn) {
        return 'points';
    }
    else {
        return '';
    }
}

export const RegisteredPlayers = (props: { players: HistoricPlayer[], events: TournamentEvent[], showAll?: boolean }) => {

    const registrationMap = new Map<number, number>();

    props.events.forEach(p => {
        if (p.event == 'player registered') {
            registrationMap.set(parseInt(p.details), p.timestamp)
        }
    });

    return (
        <table className='table table-striped tournament-events-table'>
            <thead>
            <tr>
                <th>Player Id</th>
                <th>Player Name</th>
                <th>Player Tournament Account</th>
                <th>Buy-in</th>
                <th>Date</th>
            </tr>
            </thead>
            <tbody>
            {
                props.players.map((p: HistoricPlayer, i, elements) => {
                    if (i > 9 && !props.showAll) {
                        return false;
                    }

                    const date = registrationMap.has(p.id) ? formatDate(registrationMap.get(p.id)) : '';

                    return (
                        <tr key={p.id}>
                            <td><Link to={'/user/' + p.id}>{p.id}</Link></td>
                            <td>{p.name}</td>
                            <td>{p.sessionId}</td>
                            <td>{getBuyIn(p)}</td>
                            <td>{date}</td>
                        </tr>
                    );
                })
            }
            </tbody>
        </table>
    );
};



// WEBPACK FOOTER //
// ./src/app/ui/poker/history/HistoricTournamentComponent.tsx